import React, { useState } from 'react'
import { Alert, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { translateBomLibraryError } from './bomLibraryErrors'

/**
 * BomSaveModeDialog — confirms how a structure edit is saved (CU-03.2 / D13). The mode selector only
 * appears when N5 (`getBomVersionSaveMode`) allows both OVERWRITE and NEW_VERSION; the index selector
 * only when the outcome is a new version (RN-04.10 / D16: the user picks the index of a version
 * created in the library, among the ones `indexValues/available` grants).
 * @param {string[]} props.allowedSaveModes Modes Sun allows right now (`BomSaveModeOptionsDto`, or the 409 E-BOM-10 body)
 * @param {Array<{id: string, name: string}>} props.indexOptions Result of `indexValues/available`
 * @param {string} props.defaultIndexValue Index preselected for a new version (the edited version's own)
 * @param {string} [props.error] Business literal of a previous failed attempt (E-BOM-10)
 * @param {boolean} [props.useIndex] Whether the index dimension is active (USE_WORKFLOW_INDEX, PO
 * adjustment 2026-09-10): with it off no index is asked for, the new version is born APPROVED
 * (RN-G20) and `BomVersionStructure` decides the value it sends
 * @param {(mode: string, indexValue: string) => void} props.onConfirm
 */
const BomSaveModeDialog = ({ allowedSaveModes = [], indexOptions = [], defaultIndexValue = '', error = '', useIndex = true, onConfirm, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [mode, setMode] = useState(allowedSaveModes[0] || '')
  const [indexValue, setIndexValue] = useState(defaultIndexValue)

  return (
    <SmartDialog
      setOpen
      title={{ render: <Typography variant='h6'>{t_('Save changes')}</Typography> }}
      acceptLabel={mode === 'OVERWRITE' ? 'Overwrite' : 'Create new version'}
      acceptCallback={() => onConfirm(mode, indexValue)}
      cancelCallback={onClose}
      autoClose={false}
      renderComponent={
        <Stack spacing={2} sx={{ mt: 1, minWidth: 280 }} data-testid='bom-save-mode-dialog'>
          {error && <Alert severity='warning'>{translateBomLibraryError(t_, error)}</Alert>}
          {allowedSaveModes.length > 1 && (
            <TextField select label={t_('Save mode')} value={mode} onChange={(event) => setMode(event.target.value)}>
              {allowedSaveModes.map((option) => (
                <MenuItem key={option} value={option}>
                  {t_(option === 'OVERWRITE' ? 'Overwrite' : 'Create new version')}
                </MenuItem>
              ))}
            </TextField>
          )}
          {useIndex && mode === 'NEW_VERSION' && (
            <TextField select label={t_('Index')} value={indexValue} onChange={(event) => setIndexValue(event.target.value)}>
              {indexOptions.map((option) => (
                <MenuItem key={option.id} value={option.name}>{option.name}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      }
    />
  )
}

export default BomSaveModeDialog
